import { useEffect, useRef, useState } from 'react';
import { callManualEntry, reserveManualEntry } from './manualEntryApi';

export function useManualEntryLease(enabled: boolean) {
  const [owner] = useState(() => Array.from(crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join(''));
  const [until, setUntil] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [calling, setCalling] = useState(false);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const callingRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    let renewing = false;
    const renew = () => {
      if (renewing || callingRef.current) return;
      renewing = true;
      queue.current = queue.current.catch(() => undefined).then(async () => {
        if (!alive) return;
        const started = Date.now();
        try {
          await reserveManualEntry(owner, true);
          if (alive) { setUntil(started + 70_000 > Date.now() ? started + 70_000 : 0); setError(null); }
        } catch (e) {
          if (alive) {
            setUntil(0);
            setError((e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'tel.pult.unavailable');
          }
        }
      }).finally(() => { renewing = false; });
    };
    queueMicrotask(renew);
    const timer = window.setInterval(() => { setUntil(value => value > Date.now() ? value : 0); renew(); }, 15_000);
    const focus = () => { setUntil(0); renew(); };
    window.addEventListener('focus', focus);
    return () => {
      alive = false;
      window.clearInterval(timer); window.removeEventListener('focus', focus);
      // Requests stay ordered through close/reopen, slow acquisition and call handoff.
      queue.current = queue.current.catch(() => undefined).then(async () => {
        setUntil(0);
        await reserveManualEntry(owner, false);
      }).catch(() => undefined);
    };
  }, [enabled, owner]);
  const call = async (number: string, lineId: number): Promise<boolean> => {
    if (!enabled || until <= Date.now() || callingRef.current) return false;
    callingRef.current = true; setCalling(true);
    const result = queue.current.catch(() => undefined).then(async () => {
      try {
        // Server retains the reservation through creation of the normal call busy flag.
        await callManualEntry(owner, number, lineId);
        await reserveManualEntry(owner, false).catch(() => undefined);
        setUntil(0); setError(null);
        return true;
      } catch (e) {
        setError((e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'tel.pult.unavailable');
        return false;
      } finally { callingRef.current = false; setCalling(false); }
    });
    queue.current = result;
    return result;
  };
  return { ready: enabled && until > 0 && !calling, calling, error, call };
}
